import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import CircleStyle from "ol/style/Circle";
import RegularShape from "ol/style/RegularShape";
import Icon from "ol/style/Icon";

// Esri mide símbolos en puntos; ArcGIS renderiza a 96 DPI (1pt = 96/72 px).
// OL mide en píxeles y su `radius` es radio, no diámetro.
export const PT_TO_PX = 4 / 3;

// `radiusFactor`: circunradio de RegularShape en función del diámetro que pide
// Esri. No es 0.5 para todas — un cuadrado inscrito en el circunradio r mide
// r*√2 de lado, no 2r, y por eso las formas no circulares salían chicas.
const SMS_SHAPES = {
  esriSMSSquare:   { points: 4, angle: Math.PI / 4, radiusFactor: Math.SQRT1_2 },
  esriSMSDiamond:  { points: 4, angle: 0,           radiusFactor: 0.5 },
  esriSMSTriangle: { points: 3, angle: 0,           radiusFactor: 1 / Math.sqrt(3) },
  esriSMSCross:    { points: 4, radius2: 0, angle: 0,           radiusFactor: 0.5 },
  esriSMSX:        { points: 4, radius2: 0, angle: Math.PI / 4, radiusFactor: Math.SQRT1_2 },
};

// ArcGIS dibuja los guiones con su patrón interno; OL exige un arreglo explícito
// en píxeles y no hay equivalencia exacta (fase y largo divergen: aceptado y
// documentado, candidato a hotfix de apariencia). Los patrones son
// proporcionales al ancho: con uno fijo en px, un trazo de 6pt tapa los huecos y
// se ve sólido.
const LINE_DASH = {
  esriSLSDash:       w => [4 * w, 3 * w],
  esriSLSDot:        w => [w, 2 * w],
  esriSLSDashDot:    w => [4 * w, 2 * w, w, 2 * w],
  esriSLSDashDotDot: w => [4 * w, 2 * w, w, 2 * w, w, 2 * w],
};

// Estilos de línea que el editor ofrece y el traductor sabe dibujar. Vive acá y
// no en SymbologyLineEditor.vue para que LayersSymbologies.vue pueda normalizar
// contra la lista sin importar un componente.
export const LINE_STYLE_VALUES = ["esriSLSSolid", "esriSLSDash", "esriSLSDot", "esriSLSDashDot", "esriSLSDashDotDot"];

// `width` ya viene en píxeles (pt convertidos), no en puntos.
export function lineDashFor(style, width) {
  const pattern = LINE_DASH[style];
  return pattern ? pattern(width) : undefined;
}

// Respaldo del camino "Predeterminada" del visor: lo mismo que `layers.js`
// dibujaba cuando no había renderer o el símbolo era desconocido. En el camino
// de simbología elegida NO se pasa: ocultar lo que no matchea es decisión del
// editor, y la leyenda lo refleja.
const FALLBACK_FILL = new Fill({ color: "#3399CC" });
const FALLBACK_STROKE = new Stroke({ color: "#333333", width: 1 });

// `image` no es decorativo: OL dibuja Point/MultiPoint sólo desde image/text, así
// que sin él toda feature de punto que cae al respaldo queda invisible en vez de
// azul —y el respaldo existe justo para que nada desaparezca—.
export const DEFAULT_STYLE = new Style({
  fill: FALLBACK_FILL,
  stroke: FALLBACK_STROKE,
  image: new CircleStyle({ radius: 5, fill: FALLBACK_FILL, stroke: FALLBACK_STROKE }),
});

// Esri: [r, g, b, a] con a en 0-255. null/ausente = transparente.
export function esriColor(color) {
  if (!Array.isArray(color) || color.length < 3) return "rgba(0,0,0,0)";
  const alpha = color.length > 3 ? color[3] / 255 : 1;
  return `rgba(${color[0]},${color[1]},${color[2]},${alpha})`;
}

// Un solo símbolo por defecto por geometría: hoy está duplicado en
// LayersSymbologies.vue (DEFAULT_SYMBOL) y SymbologySymbolEditor.vue
// (DEFAULT_SMS); con líneas serían cuatro copias.
const DEFAULT_SYMBOLS = {
  esriGeometryPoint: {
    type: "esriSMS",
    style: "esriSMSCircle",
    color: [227, 139, 79, 255],
    size: 8,
    outline: { type: "esriSLS", style: "esriSLSSolid", color: [0, 0, 0, 255], width: 1 },
  },
  esriGeometryPolyline: {
    type: "esriSLS",
    style: "esriSLSSolid",
    color: [227, 139, 79, 255],
    width: 2,
  },
  esriGeometryPolygon: {
    type: "esriSFS",
    style: "esriSFSSolid",
    color: [227, 139, 79, 128],
    outline: { type: "esriSLS", style: "esriSLSSolid", color: [0, 0, 0, 255], width: 1 },
  },
};

// Copia profunda: sin ella dos filas del editor compartirían el mismo objeto.
export function defaultSymbolFor(geometryType) {
  const symbol = DEFAULT_SYMBOLS[geometryType] || DEFAULT_SYMBOLS.esriGeometryPoint;
  return JSON.parse(JSON.stringify(symbol));
}

function toHex(component) {
  return Math.max(0, Math.min(255, Math.round(component || 0))).toString(16).padStart(2, "0");
}

// `<input type="color">` habla hex; Esri habla [r,g,b,a] con a en 0-255.
export function rgbToHex(color) {
  const rgb = Array.isArray(color) ? color : [0, 0, 0];
  return "#" + toHex(rgb[0]) + toHex(rgb[1]) + toHex(rgb[2]);
}

export function hexToRgb(hex) {
  const value = parseInt((hex || "#000000").slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

// Paletas del editor de simbologías. Hex verbatim de ColorBrewer / Tableau /
// Viridis: arreglos de strings, cero dependencias nuevas. Cualitativas para
// uniqueValue y secuenciales para classBreaks, separadas porque una cualitativa
// sobre rangos produce un coroplético ilegible.
export const QUALITATIVE_PALETTES = [
  { id: "dark2", name: "Categórica", colors: ["#1b9e77", "#d95f02", "#7570b3", "#e7298a", "#66a61e", "#e6ab02", "#a6761d", "#666666"] },
  { id: "set2", name: "Categórica suave", colors: ["#66c2a5", "#fc8d62", "#8da0cb", "#e78ac3", "#a6d854", "#ffd92f", "#e5c494", "#b3b3b3"] },
  { id: "tableau10", name: "Contraste alto", colors: ["#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f", "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac"] },
];

export const SEQUENTIAL_PALETTES = [
  { id: "viridis", name: "Amarillo-morado", colors: ["#fde725", "#addc30", "#5ec962", "#28ae80", "#21918c", "#2c728e", "#3b528b", "#472d7b", "#440154"] },
  { id: "ylorrd", name: "Amarillo-rojo", colors: ["#ffffcc", "#ffeda0", "#fed976", "#feb24c", "#fd8d3c", "#fc4e2a", "#e31a1c", "#bd0026", "#800026"] },
  { id: "blues", name: "Azules", colors: ["#f7fbff", "#deebf7", "#c6dbef", "#9ecae1", "#6baed6", "#4292c6", "#2171b5", "#08519c", "#08306b"] },
];

// Cicla: con 30 comunas y 8 colores se repiten cada 8. Es lo estándar y sigue
// siendo mejor que 30 naranjas idénticos.
export function qualitativeColor(colors, index) {
  return colors[index % colors.length];
}

// Interpola en vez de tomar las primeras N muestras: con 3 rangos sobre Viridis,
// las primeras tres muestras son tres morados casi idénticos. Así la clase i de N
// cae en i/(N-1) del gradiente y siempre se usan los dos extremos. total <= 1 cae
// en t = 0 para no dividir por cero.
export function sequentialColor(colors, index, total) {
  const t = total > 1 ? index / (total - 1) : 0;
  const position = t * (colors.length - 1);
  const low = Math.floor(position);
  const from = hexToRgb(colors[low]);
  const to = hexToRgb(colors[Math.min(low + 1, colors.length - 1)]);
  const fraction = position - low;
  return rgbToHex([0, 1, 2].map(channel => from[channel] + (to[channel] - from[channel]) * fraction));
}

// Escribe SÓLO el relleno y preserva la alfa que el símbolo ya tenía: imponer
// a=255 dejaría toda simbología de polígono opaca tapando el mapa base (nace en
// 128), el defecto que transparentize() y D22 ya pelearon. El contorno no se
// toca: el borde negro fino es lo que separa polígonos vecinos. Un esriPMS es un
// PNG base64 sin `color`; escribírselo dejaría una llave muerta viajando a
// MapServer, y quien puso un ícono ya eligió su apariencia.
export function applyColor(symbol, hex) {
  if (!symbol || symbol.type === "esriPMS") return;
  const alpha = Array.isArray(symbol.color) && symbol.color.length > 3 ? symbol.color[3] : 255;
  symbol.color = hexToRgb(hex).concat(alpha);
}

// Trazo OL de un símbolo esriSLS. null = sin trazo: `esriSLSNull` o símbolo
// ausente. El ancho va en puntos tipográficos (D13), igual que el contorno de
// los símbolos de punto: es lo único que hace coincidir el camino FeatureServer
// (OL, cliente) con el camino MapServer (dynamicLayers, servidor).
function esriLineStroke(symbol) {
  if (!symbol || symbol.style === "esriSLSNull") return null;
  // Piso de 1pt como hacía `layers.js` antes de unificar: en Esri un ancho 0 es
  // "el trazo más fino", no "sin trazo" —para eso está esriSLSNull—, y canvas
  // ignora un lineWidth de 0 y hereda el ancho del dibujo anterior.
  const width = (symbol.width || 1) * PT_TO_PX;
  return new Stroke({
    color: esriColor(symbol.color),
    width,
    lineDash: lineDashFor(symbol.style, width),
  });
}

export function symbolToOlStyle(symbol) {
  if (!symbol) return undefined;

  if (symbol.type === "esriPMS") {
    const src = symbol.imageData
      ? `data:${symbol.contentType || "image/png"};base64,${symbol.imageData}`
      : symbol.url;
    if (!src) return undefined;
    // ponytail: scale 1 fijo. El editor hornea el tamaño final en el PNG y no
    // emite width/height, así OL (tamaño natural) y ArcGIS (tamaño natural a
    // 96 DPI) dibujan igual. Si algún día se aceptan esriPMS con width/height
    // de un servicio externo, hay que leer el tamaño natural de la imagen.
    return new Style({ image: new Icon({ src, scale: 1 }) });
  }

  if (symbol.type === "esriSLS") {
    const stroke = esriLineStroke(symbol);
    const style = new Style({});
    if (stroke) style.setStroke(stroke);
    return style;
  }

  // El traductor sabe polígonos aunque el editor no los ofrezca (TR-04c): sin
  // esta rama, toda capa FeatureServer de polígonos del catálogo caería al
  // respaldo azul al unificar.
  if (symbol.type === "esriSFS") {
    const stroke = esriLineStroke(symbol.outline);
    const fill = symbol.style === "esriSFSNull" ? "rgba(0,0,0,0)" : esriColor(symbol.color);
    const style = new Style({ fill: new Fill({ color: fill }) });
    if (stroke) style.setStroke(stroke);
    return style;
  }

  if (symbol.type !== "esriSMS") return undefined;

  const fill = new Fill({ color: esriColor(symbol.color) });
  const stroke = symbol.outline
    ? new Stroke({ color: esriColor(symbol.outline.color), width: (symbol.outline.width || 0) * PT_TO_PX })
    : undefined;
  // Esri `size` es el ancho total del símbolo. Para el círculo eso es el
  // diámetro; para RegularShape hay que convertirlo a circunradio (ver
  // radiusFactor), que no es lo mismo.
  const diameter = (symbol.size || 8) * PT_TO_PX;
  const shape = SMS_SHAPES[symbol.style];

  if (!shape) return new Style({ image: new CircleStyle({ fill, stroke, radius: diameter / 2 }) });

  const { radiusFactor, ...options } = shape;
  options.radius = diameter * radiusFactor;

  // Cruz y equis degeneran en cuatro segmentos (radius2: 0): con área nula el
  // fill no pinta nada y sólo se vería el contorno, o sea el color equivocado.
  // ArcGIS dibuja los brazos con el color del símbolo, así que el relleno viaja
  // como trazo; el ancho sale del "Contorno" que el editor ya expone, con 1pt
  // de piso para que un contorno en 0 no deje el símbolo invisible.
  if (shape.radius2 === 0) {
    options.stroke = new Stroke({
      color: esriColor(symbol.color),
      width: ((symbol.outline && symbol.outline.width) || 1) * PT_TO_PX,
    });
  } else {
    options.fill = fill;
    options.stroke = stroke;
  }

  return new Style({ image: new RegularShape(options) });
}

// Devuelve una función de estilo OL para el renderer. `fallbackStyle` es lo que
// se dibuja cuando no hay renderer, el tipo de renderer es desconocido
// (heatmap, dotDensity, vectorField, CIM), el símbolo no se sabe traducir o la
// feature no matchea. Sin él (camino de simbología elegida) esos casos devuelven
// undefined, que en OL significa feature invisible.
export function createStyleFunction(renderer, fallbackStyle) {
  if (!renderer) return () => fallbackStyle;

  if (renderer.type === "simple") {
    const style = symbolToOlStyle(renderer.symbol) || fallbackStyle;
    return () => style;
  }

  if (renderer.type === "uniqueValue") {
    const styles = new Map();
    (renderer.uniqueValueInfos || []).forEach(info => {
      // String() en set y get: Esri guarda strings, EsriJSON devuelve numbers.
      styles.set(String(info.value), symbolToOlStyle(info.symbol) || fallbackStyle);
    });
    const fallback = (renderer.defaultSymbol && symbolToOlStyle(renderer.defaultSymbol)) || fallbackStyle;
    return feature => {
      const raw = feature.get(renderer.field1);
      return styles.get(raw == null ? "" : String(raw)) || fallback;
    };
  }

  if (renderer.type === "classBreaks") {
    // Orden ascendente propio: el matcher no puede depender del orden del arreglo.
    const infos = [...(renderer.classBreakInfos || [])].sort((a, b) => a.classMaxValue - b.classMaxValue);
    const styles = infos.map(info => symbolToOlStyle(info.symbol) || fallbackStyle);
    const floor = renderer.minValue != null ? renderer.minValue : -Infinity;
    return feature => {
      const value = Number(feature.get(renderer.field));
      if (!Number.isFinite(value) || value < floor) return fallbackStyle;
      const index = infos.findIndex(info => value <= info.classMaxValue);
      return index === -1 ? fallbackStyle : styles[index];
    };
  }

  return () => fallbackStyle;
}

// Lista de swatches (etiqueta + símbolo) para la leyenda del visor y el
// preview del editor: un solo origen de verdad para ambos.
export function rendererSwatches(renderer) {
  if (!renderer) return [];
  switch (renderer.type) {
    case "simple":
      return [{ label: renderer.label || "", symbol: renderer.symbol }];
    case "uniqueValue": {
      const rows = (renderer.uniqueValueInfos || []).map(info => ({
        label: info.label || String(info.value),
        symbol: info.symbol,
      }));
      // createStyleFunction pinta con defaultSymbol toda feature fuera de la
      // lista y el editor siempre lo escribe: sin esta fila la leyenda callaría
      // un símbolo que sí está en el mapa.
      if (renderer.defaultSymbol) {
        rows.push({ label: renderer.defaultLabel || "Otros", symbol: renderer.defaultSymbol });
      }
      return rows;
    }
    case "classBreaks":
      return (renderer.classBreakInfos || []).map(info => ({
        label: info.label || `≤ ${info.classMaxValue}`,
        symbol: info.symbol,
      }));
    default:
      return [];
  }
}

// Formato de despliegue de un corte. es-CL como el resto de la app
// (AttributesTable, Measure, drawMeasurements) y la audiencia es hispanohablante.
// Las etiquetas son texto: ArcGIS no las parsea, el separador de miles no
// arriesga nada en dynamicLayers.
export function formatBreakNumber(value, decimals) {
  const number = Number(value);
  // Un renderer legado puede traer un campo no numérico o un classMaxValue nulo:
  // 'NaN' en la leyenda es peor que una etiqueta vacía, y rendererSwatches ya
  // tiene su respaldo `≤ ${classMaxValue}` para ese caso.
  if (!Number.isFinite(number)) return "";
  return number.toLocaleString("es-CL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Etiquetas de rango para las seis vías de clasificación, incluida Manual.
// Devuelve un arreglo de strings alineado con `infos` TAL COMO VIENE (no lo
// reordena ni lo muta): el piso de cada fila sale de una copia ordenada, para
// que una fila tecleada fuera de orden muestre el rango que va a tener después
// de guardar — normalizedDraft() ordena al guardar y las filas del DOM no se
// reordenan en vivo.
export function formatBreakLabels(infos, minValue, decimals) {
  // Mismo defensivo que rendererSwatches (`renderer.classBreakInfos || []`):
  // editSymbology() clona el renderer guardado sin pasar por isSeedUsable(), y
  // un renderer legado sin la lista llegaría acá en el open.
  const list = Array.isArray(infos) ? infos : [];
  const floor = Number(minValue);
  const hasFloor = minValue !== "" && minValue != null && Number.isFinite(floor);
  // Índices ordenados y escritura por índice, NO `sorted.indexOf(info)`: con dos
  // filas del mismo classMaxValue `indexOf` puede devolver el slot de cualquiera
  // de las dos, y la que resolviera a rank 0 se llevaría el prefijo de minValue
  // aunque esté al medio de la lista. Es el mismo idioma índice→rango que
  // setPalette() ya usa en esta vista (LayersSymbologies.vue).
  const order = list.map((info, index) => index)
    .sort((a, b) => Number(list[a].classMaxValue) - Number(list[b].classMaxValue));
  const labels = new Array(list.length);
  order.forEach((arrayIndex, rank) => {
    const top = formatBreakNumber(list[arrayIndex].classMaxValue, decimals);
    labels[arrayIndex] = rank === 0
      // Sin piso finito la primera clase no tiene borde inferior que mostrar:
      // createStyleFunction usa -Infinity ahí mismo.
      ? (hasFloor ? `${formatBreakNumber(floor, decimals)} - ${top}` : `≤ ${top}`)
      : `${formatBreakNumber(list[order[rank - 1]].classMaxValue, decimals)} - ${top}`;
  });
  return labels;
}

// Decimales de UNA etiqueta guardada. El separador decimal es el que aparece
// MÁS A LA DERECHA del label: es-CL usa coma decimal tras el punto de miles;
// un renderer legado en-US usa el orden inverso ("1,234.5"). Si el punto queda
// a la derecha de la coma, el label no es es-CL: no se infiere de forma
// confiable y se cae a la regla de la decisión 5 (spec QA-25b §5).
export function inferDecimalsFromLabel(label) {
  const text = String(label || "");
  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  if (lastComma === -1 || lastDot > lastComma) return null;
  const digits = /^\d+/.exec(text.slice(lastComma + 1));
  if (!digits) return null;
  return Math.min(4, digits[0].length);
}

// Defecto para una lista recién clasificada: 0 si todos los techos son enteros,
// 2 si no. Evita que un campo de población salga como "1.234,00" sin que nadie
// haya pedido decimales.
export function inferDecimals(infos) {
  const list = Array.isArray(infos) ? infos : [];
  const allIntegers = list.every(info => Number.isInteger(Number(info.classMaxValue)));
  return allIntegers ? 0 : 2;
}

// Parámetro dynamicLayers del endpoint /export de ArcGIS MapServer.
// El renderer NO va pelado: va dentro de un objeto DynamicLayer con id y
// source. Verificado contra ArcGIS real: sin `source` responde
// 400 "Missing 'source' in dynamic layer definition".
//
// definitionExpression es el filtro de la capa. Va acá dentro y no en LAYERDEFS
// porque /export IGNORA layerDefs cuando recibe dynamicLayers: con una
// simbología personalizada activa, el filtro seguía viajando en la URL pero el
// servidor lo descartaba y la imagen del mapa salía sin filtrar (QA-176).
export function dynamicLayersParam(codigo, renderer, definitionExpression = null) {
  const id = Number(codigo) || 0;
  const dynamicLayer = { id, source: { type: "mapLayer", mapLayerId: id }, drawingInfo: { renderer } };
  // Sin filtro se omite la clave: un definitionExpression vacío es una cláusula
  // inválida, no "sin filtro".
  if (definitionExpression) dynamicLayer.definitionExpression = definitionExpression;
  return JSON.stringify([dynamicLayer]);
}
